import { rootReducer } from "@redux/reducer/rootReducer";
import { type Action, configureStore, type ThunkAction } from "@reduxjs/toolkit";

import { type FeatureFlagState } from "./reducer/featureFlagSlice";
import { type LicenseTenantState } from "./reducer/licenseTenantSlice";
import { type SystemSettingState } from "./reducer/systemSettingSlice";
import { type UserState } from "./reducer/userSlice";
import { type WorkflowState } from "./reducer/workflowSlice";

import { createErrorBoundaryMiddleware } from "@/adapters/base/ErrorBoundaryMiddleware";
import Logger from "@/helper/logger";
import { announcementApi } from "@/services/announcement";
import { apiKeyApi } from "@/services/apiKey";
import { jobApi } from "@/services/file";
import { apiIpWhitelist } from "@/services/ipWhitelist";
import { llmSampleApi } from "@/services/llmSample";
import { metadataApi } from "@/services/metadata";
import { pipelineApi } from "@/services/pipeline";
import { retentionPoliciesApi } from "@/services/retentionPolicy";
import { statisticApi } from "@/services/statistic";
import { systemSettingApi } from "@/services/systemSetting";
import { teamApi } from "@/services/team";
import { templateApi } from "@/services/template";
import { tenantConfigApi } from "@/services/tenantConfig";
import { userApi } from "@/services/user";
import { workflowApi } from "@/services/workflow";

// Create error boundary middleware with configuration
const errorBoundaryMiddleware = createErrorBoundaryMiddleware({
  enableLogging: Logger.shouldLog(),
  enableFallbacks: Logger.shouldLog(),
  fallbackData: {
    // Add fallback data for specific endpoints
    getAPIKeys: [],
    getWorkflows: [],
    getUsers: [],
  },
  errorHandlers: {
    // Add custom error handlers for specific endpoints
    getAPIKeys: (error) => {
      // eslint-disable-next-line no-console
      console.error("[Store] API Keys error:", error);
      return error;
    },
  },
});

const store = configureStore({
  reducer: rootReducer,
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: true,
    }).concat(
      errorBoundaryMiddleware,
      metadataApi.middleware,
      workflowApi.middleware,
      jobApi.middleware,
      pipelineApi.middleware,
      templateApi.middleware,
      llmSampleApi.middleware,
      teamApi.middleware,
      userApi.middleware,
      statisticApi.middleware,
      apiKeyApi.middleware,
      apiIpWhitelist.middleware,
      systemSettingApi.middleware,
      retentionPoliciesApi.middleware,
      tenantConfigApi.middleware,
      announcementApi.middleware
    ),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

export const userState = (state: RootState): UserState => state.user;
export const workflowState = (state: RootState): WorkflowState => state.workflow;
export const featureFlagState = (state: RootState): FeatureFlagState => state.featureFlag;
export const licenseTenantState = (state: RootState): LicenseTenantState => state.licenseTenant;
export const systemSettingState = (state: RootState): SystemSettingState => state.systemSetting;

export default store;
